import { useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCalendar, FiChevronLeft, FiChevronRight, FiPlus, FiSave } from 'react-icons/fi'
import { StatusChip, StickyActionFooter } from '../../shared'
import { AutoBuildDialog } from './AutoBuildDialog'
import { DraftRecoveryBanner } from './DraftRecoveryBanner'
import { ScheduleConflictBanner } from './ScheduleConflictBanner'
import { CreateMeetingPopover } from './CreateMeetingPopover'
import type { CreateMeetingContext } from './CreateMeetingPopover'
import { MeetingDetailSheet } from './MeetingDetailSheet'
import { AttendeePickerSheet } from './AttendeePickerSheet'
import { LegislatorCoveragePanel } from './LegislatorCoveragePanel'
import {
  DAY_START_MINUTES, computeValidity, constituencyFor, minutesToTime, timeToMinutes,
} from './lobbyScheduleUtils'
import type { useLobbyScheduleEditor } from '../../../hooks/useLobbyScheduleEditor'
import { useAuth } from '../../../hooks/useAuth'
import type {
  MeetingValidity, ScheduleAttendeeDto, ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTimeLabel(time: string): string {
  const minutes = timeToMinutes(time)
  const h24 = Math.floor(minutes / 60)
  const m = minutes % 60
  const h12 = ((h24 + 11) % 12) + 1
  return `${h12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`
}

const VALIDITY_COLOR: Record<MeetingValidity, string> = {
  Valid: 'green', Unverified: 'yellow', NoConstituent: 'red', Empty: 'gray',
}

/** Validity of a meeting driven by its CURRENT (locally-edited) assignments — mirrors
 *  LobbyScheduler's meetingValidityLocal. Exported for reuse by the detail sheet (Task 6). */
export function meetingValidityColor(
  meeting: ScheduleMeetingDto,
  legislatorById: Map<string, ScheduleLegislatorRowDto>,
  attendeeById: Map<string, ScheduleAttendeeDto>,
): { validity: MeetingValidity; color: string } {
  const legislator = legislatorById.get(meeting.legislatorId)
  const statuses = legislator
    ? meeting.assignments.map((a) => {
        const att = attendeeById.get(a.attendeeId)
        return att ? constituencyFor(att, legislator) : ('Unknown' as const)
      })
    : []
  const validity = computeValidity(statuses)
  return { validity, color: VALIDITY_COLOR[validity] }
}

interface LobbyScheduleAgendaEditorProps {
  editor: ReturnType<typeof useLobbyScheduleEditor>
  onOpenMeeting?: (meetingId: string) => void
}

export function LobbyScheduleAgendaEditor({ editor, onOpenMeeting }: LobbyScheduleAgendaEditorProps) {
  const {
    schedule, legislators, attendees, asks, meetings, startDate, endDate,
    selectedDate, setSelectedDate, isLoading, error, isDirty, isSaving, createMeeting, save,
    assign, unassign, move, deleteMeeting, reschedule, autoBuildTargetCount, autoBuildItinerary,
    setMeetingAsk, setMeetingRoom, setMeetingStatus, setAttendance, recoverableDraft, restoreDraft, discardDraft,
    conflict, reloadLatest, setMeetingWithWhom, setRole, setMyStaffTag,
  } = editor
  const { user } = useAuth()

  const [popover, setPopover] = useState<CreateMeetingContext | null>(null)
  const [openMeetingId, setOpenMeetingId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const autoBuildDisclosure = useDisclosure()

  const legislatorById = useMemo(
    () => new Map(legislators.map((l) => [l.legislatorId, l] as const)), [legislators])
  const attendeeById = useMemo(
    () => new Map(attendees.map((a) => [a.attendeeId, a] as const)), [attendees])

  const openMeeting = openMeetingId ? meetings.find((m) => m.id === openMeetingId) ?? null : null
  const openLegislator = openMeeting ? legislatorById.get(openMeeting.legislatorId) : undefined
  const assignedIds = useMemo(
    () => new Set(openMeeting?.assignments.map((a) => a.attendeeId) ?? []),
    [openMeeting])

  const date = selectedDate ?? startDate ?? ''
  const showDaySwitcher = !!endDate && !!startDate && endDate !== startDate

  // Meetings for the selected day, grouped by start-time block, same grouping the desktop agenda used.
  const blocks = useMemo(() => {
    const dayMeetings = meetings.filter((m) => m.meetingDate === date)
    const byTime = new Map<string, ScheduleMeetingDto[]>()
    for (const m of dayMeetings) {
      if (!byTime.has(m.startTime)) byTime.set(m.startTime, [])
      byTime.get(m.startTime)!.push(m)
    }
    return [...byTime.entries()].sort(([a], [b]) => timeToMinutes(a) - timeToMinutes(b))
  }, [meetings, date])

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error || !schedule) return <Text color="red.500">Failed to load schedule.</Text>

  const openCreate = () => setPopover({
    legislatorId: legislators[0]?.legislatorId ?? '',
    slotTime: minutesToTime(DAY_START_MINUTES),
    pick: true,
  })

  return (
    <Box pb="96px">
      {showDaySwitcher && (
        <HStack mb={3} gap={2} justify="center">
          <IconButton
            aria-label="Previous day"
            size="sm"
            variant="outline"
            minW="44px"
            h="44px"
            disabled={date <= (startDate ?? '')}
            onClick={() => setSelectedDate(addDays(date, -1))}><FiChevronLeft /></IconButton>
          <Text fontWeight="medium" minW="150px" textAlign="center">{formatDateLabel(date)}</Text>
          <IconButton
            aria-label="Next day"
            size="sm"
            variant="outline"
            minW="44px"
            h="44px"
            disabled={date >= (endDate ?? '')}
            onClick={() => setSelectedDate(addDays(date, 1))}><FiChevronRight /></IconButton>
        </HStack>
      )}

      <Box mb={3}>
        <LegislatorCoveragePanel
          eventId={schedule.eventId}
          meetings={meetings}
          defaultOpen={false}
          onAddMeeting={(legislatorId) =>
            setPopover({ legislatorId, slotTime: minutesToTime(DAY_START_MINUTES), pick: true })}
        />
      </Box>

      <Button
        w="full"
        size="sm"
        variant="outline"
        colorPalette="brand"
        mb={2}
        minH="44px"
        onClick={autoBuildDisclosure.onOpen}><FiCalendar />Auto-build itinerary
              </Button>

      <Button
        w="full"
        size="sm"
        variant="outline"
        colorPalette="brand"
        mb={4}
        disabled={legislators.length === 0}
        onClick={openCreate}><FiPlus />New meeting
              </Button>

      {conflict && <ScheduleConflictBanner onReload={reloadLatest} />}

      {recoverableDraft && (
        <DraftRecoveryBanner onRestore={restoreDraft} onDiscard={discardDraft} />
      )}

      {blocks.length === 0 ? (
        <Text color="gray.500" fontSize="sm" textAlign="center" py={8}>No meetings scheduled for this day yet.</Text>
      ) : (
        <VStack align="stretch" gap={4}>
          {blocks.map(([time, dayMeetings]) => (
            <Box key={time}>
              <Heading size="xs" color="gray.500" mb={2}>{formatTimeLabel(time)}</Heading>
              <VStack align="stretch" gap={2}>
                {dayMeetings.map((m) => {
                  const legislator = legislatorById.get(m.legislatorId)
                  const { color } = meetingValidityColor(m, legislatorById, attendeeById)
                  const noShowCount = m.assignments.filter((a) => a.attendance === 'Absent').length
                  const lateCount = m.assignments.filter((a) => a.attendance === 'Late').length
                  return (
                    <Box
                      textAlign="left"
                      w="full"
                      bg="surface"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderLeftWidth="4px"
                      borderLeftColor={`${color}.400`}
                      borderRadius="md"
                      p={3}
                      shadow="sm"
                      minH="64px"
                      _active={{ bg: 'gray.50' }}
                      asChild><button
                            key={m.id}
                            onClick={() => { setOpenMeetingId(m.id); onOpenMeeting?.(m.id) }}>
                            <HStack justify="space-between" align="flex-start">
                              <Text fontWeight="medium" lineClamp={1}>{legislator?.fullName ?? 'Unknown legislator'}</Text>
                              <HStack gap={1} flexShrink={0}>
                                {noShowCount > 0 && (
                                  <Badge colorPalette="red" fontSize="2xs">{noShowCount} no-show{noShowCount === 1 ? '' : 's'}</Badge>
                                )}
                                {lateCount > 0 && (
                                  <StatusChip variant="warning">{lateCount} late</StatusChip>
                                )}
                                {m.status === 'DropOff' && (
                                  <StatusChip variant="warning">Drop-off</StatusChip>
                                )}
                                <Badge colorPalette={color}>{m.assignments.length} assigned</Badge>
                              </HStack>
                            </HStack>
                            <Text fontSize="sm" color="gray.500" lineClamp={1}>{m.room ?? 'No room set'}</Text>
                            {/* LWFS-1: mobile parity with the desktop MeetingAgendaCard — a
                                meeting with no ask attached is almost always a mistake, so flag it. */}
                            {!m.askTitle && (
                              <Text fontSize="xs" color="warning.fg" fontWeight="semibold" lineClamp={1}>⚠ No ask</Text>
                            )}
                          </button></Box>
                  );
                })}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}

      <CreateMeetingPopover
        context={popover}
        asks={asks}
        legislators={legislators}
        attendees={attendees}
        currentDay={date}
        onCreate={(meeting) => { createMeeting(meeting); setPopover(null) }}
        onClose={() => setPopover(null)}
      />

      <MeetingDetailSheet
        isOpen={openMeeting !== null}
        onClose={() => setOpenMeetingId(null)}
        meeting={openMeeting}
        legislator={openLegislator}
        allMeetings={meetings}
        legislatorById={legislatorById}
        onUnassign={(assignmentId) => { if (openMeeting) unassign(openMeeting.id, assignmentId) }}
        onMove={(assignmentId, toMeetingId, attendeeId) => { if (openMeeting) move(assignmentId, openMeeting.id, toMeetingId, attendeeId) }}
        onAddAttendee={() => setPickerOpen(true)}
        onDelete={(meetingId) => deleteMeeting(meetingId)}
        onReschedule={reschedule}
        asks={asks}
        onSetAsk={(meetingId, askId) => setMeetingAsk(meetingId, askId)}
        onSetRoom={(meetingId, room) => setMeetingRoom(meetingId, room)}
        onSetStatus={(meetingId, status) => setMeetingStatus(meetingId, status)}
        onSetAttendance={(meetingId, assignmentId, status) => setAttendance(meetingId, assignmentId, status)}
        onSetWithWhom={setMeetingWithWhom}
        onSetRole={setRole}
        currentUserId={user?.id}
        onSetMyStaffTag={setMyStaffTag}
      />

      <AttendeePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        attendees={attendees}
        legislator={openLegislator}
        assignedAttendeeIds={assignedIds}
        onSelect={(attendeeId) => { if (openMeeting) assign(openMeeting.id, attendeeId) }}
      />

      <AutoBuildDialog
        isOpen={autoBuildDisclosure.open}
        onClose={autoBuildDisclosure.onClose}
        targetCount={autoBuildTargetCount}
        onBuild={(durationMin) => {
          const s = autoBuildItinerary(durationMin)
          autoBuildDisclosure.onClose()
          toaster.create({
            title: `Created ${s.createdCount} meeting${s.createdCount === 1 ? '' : 's'}`,
            description: `${s.assignedCount} assigned · ${s.gapCount} gap${s.gapCount === 1 ? '' : 's'}${s.unplacedCount ? ` · ${s.unplacedCount} didn't fit` : ''}`,
            type: s.createdCount > 0 ? 'success' : 'info',
            duration: 4000,
          })
        }}
      />

      <StickyActionFooter>
        <Button
          colorPalette="brand"
          disabled={!isDirty || conflict}
          loading={isSaving}
          onClick={() => save()}><FiSave />Save
                  </Button>
      </StickyActionFooter>
    </Box>
  );
}
